import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NiceModal from '@ebay/nice-modal-react';
import { AppShell } from '@packages/ui/layout';
import { ThemeProvider } from '@packages/ui/theme';
import { ActionRegistry } from '@packages/table-builder';
import { registerGlobalErrorHandler } from '@packages/api-client';
import { registerConfirmErrorHandler } from '@packages/ui/integrations';
import { BrandingProvider } from '@/context/brandingContext';
import { AuthGate } from '@apps/customer/providers';
import { CustomerNavbar } from '@apps/customer/components/Navbar';
import RoutesContainer from '@/routes/RoutesContainer';
import { useAppShellGuards } from '@apps/customer/hooks/useAppShellGuards';
import { useTablePermissionResolver } from '@apps/customer/hooks/useTablePermissionResolver';
import { registerAllDomains } from '@apps/customer/domains/registry';
import { showApiErrorToast } from '@packages/shared/utils';
import { ActionIntentHandler } from './ActionIntentHandler';
import { ComposePopoverHandler } from './ComposePopoverHandler';
import ChatbotOverlay from './ChatbotOverlay';
import pingBg from '@/assets/images/pink_background.png';

// Auto-discovers all domains/[thing]/[thing].actions.ts via import.meta.glob (lazy)
registerAllDomains();

// Factory hooks (createEntityQuery/createEntityMutation) will show error toasts automatically
registerGlobalErrorHandler(showApiErrorToast);

// ConfirmDialog onAction errors will show error toasts automatically
registerConfirmErrorHandler(showApiErrorToast);

/**
 * Full authenticated layout — only rendered after AuthGate confirms a session.
 * BrandingProvider, navbar, chatbot, and guards all run safely here.
 */
const AuthenticatedContent: React.FC = () => {
  useAppShellGuards();
  useTablePermissionResolver();

  const navigate = useNavigate();
  useEffect(() => {
    ActionRegistry.setNavigate(navigate);
  }, [navigate]);

  return (
    <BrandingProvider>
      <AppShell
        navbar={<CustomerNavbar />}
        backgroundImage={pingBg}
        mainId='main-content'
        padding='none'
        overlay={<ChatbotOverlay />}>
        <ComposePopoverHandler />
        <RoutesContainer />
        {/* After routes so table/action modals stack above legacy page modals (e.g. Key Management). */}
        <ActionIntentHandler />
      </AppShell>
    </BrandingProvider>
  );
};

const CustomerAppShell: React.FC = () => {
  return (
    <ThemeProvider>
      <NiceModal.Provider>
        <AuthGate publicFallback={<RoutesContainer />}>
          <AuthenticatedContent />
        </AuthGate>
      </NiceModal.Provider>
    </ThemeProvider>
  );
};

export default CustomerAppShell;
